import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../App';
import { toast } from 'sonner';
import { Eye, EyeOff, Crown, Sparkles, WifiOff } from 'lucide-react';
import Logo from '../components/Logo';

const Login = ({ setUser, setNeedsFamilySetup }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDemoOptions, setShowDemoOptions] = useState(false);
  const [serverReady, setServerReady] = useState(true);

  // Pre-flight: wake up the backend on page load
  useEffect(() => {
    let cancelled = false;
    const wakeBackend = async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await api.get('/health');
          if (!cancelled) setServerReady(true);
          return;
        } catch {
          if (!cancelled) setServerReady(false);
          await new Promise(r => setTimeout(r, 2000));
        }
      }
    };
    wakeBackend();
    return () => { cancelled = true; };
  }, []);

  const demoAccounts = [
    { 
      email: 'demo-premium@maadabbu.com', 
      password: 'demo123', 
      label: 'Premium Demo',
      icon: Crown,
      color: 'from-amber-400 to-orange-500',
      description: '3 members, ₹1.72 Cr portfolio'
    },
    { 
      email: 'demo-free@maadabbu.com', 
      password: 'demo123', 
      label: 'Free Demo',
      icon: Sparkles,
      color: 'from-gray-400 to-gray-600',
      description: '4 members, ₹12.27 L portfolio'
    },
  ];

  const handleDemoLogin = (account) => {
    setFormData({ ...formData, email: account.email, password: account.password });
    setShowDemoOptions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const endpoint = isLogin ? '/auth/login' : '/auth/register';
    const payload = isLogin
      ? { email: formData.email, password: formData.password }
      : formData;

    // Retry up to 3 times for network/server errors
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await api.post(endpoint, payload);
        localStorage.setItem('token', res.data.access_token);
        setUser(res.data.user);
        
        // Check if user must change password first
        if (res.data.must_change_password) {
          toast.info('Please set a new password to continue');
          navigate('/change-password');
          return;
        }
        
        // Check if user needs to set up a family
        if (res.data.needs_family_setup) {
          setNeedsFamilySetup?.(true);
          toast.success(isLogin ? 'Welcome back!' : 'Account created! Let\'s set up your family.');
          navigate('/family-setup');
        } else if (res.data.families?.length > 1) {
          toast.success('Welcome back!');
          navigate('/family-picker');
        } else {
          toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
          navigate('/dashboard');
        }
        return; // Success — exit
      } catch (err) {
        lastError = err;
        // Only retry on network errors or 503 (server starting up)
        const isRetryable = !err.response || err.response.status === 503;
        if (isRetryable && attempt < 3) {
          toast.info(`Connecting to server... (attempt ${attempt + 1}/3)`);
          await new Promise(r => setTimeout(r, 1500 * attempt));
          continue;
        }
        break; // Auth errors (401, 422) — don't retry
      }
    }

    // All attempts failed
    if (lastError) {
      const msg = formatApiError(lastError);
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&w=1920&q=80')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#064E3B]/85 to-[#064E3B]/70"></div>
        <div className="relative z-10 p-12 flex flex-col justify-center text-white">
          <Logo size="hero" className="mb-6 [&_span]:text-white" />
          <p className="text-xl leading-relaxed text-gray-100 max-w-md">
            Your family&apos;s wealth, beautifully organized. Track investments, manage portfolios, and grow together.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#F9FAFB]">
        <div className="w-full max-w-md">
          {!serverReady && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="server-warming-banner">
              <WifiOff size={16} />
              <span>Connecting to server... This may take a moment.</span>
            </div>
          )}
          <div className="card p-8">
            <h2 className="text-3xl font-bold mb-2 text-center tracking-tight">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h2>
            <p className="text-gray-500 mb-8 text-center">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium mb-2" data-testid="name-label">
                    Full Name
                  </label>
                  <input
                    type="text"
                    data-testid="name-input"
                    className="input-field w-full"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required={!isLogin}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2" data-testid="email-label">
                  Email Address
                </label>
                <input
                  type="email"
                  data-testid="email-input"
                  className="input-field w-full"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" data-testid="password-label">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    data-testid="password-input"
                    className="input-field w-full pr-12"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                data-testid="submit-button"
                className="btn-primary w-full"
                disabled={loading}
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="toggle-form-button"
                className="text-sm text-primary hover:underline"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setFormData({ email: '', password: '', name: '' });
                }}
              >
                {isLogin
                  ? "Don't have an account? Sign up"
                  : 'Already have an account? Sign in'}
              </button>
            </div>

            {/* Demo Login Section */}
            {isLogin && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowDemoOptions(!showDemoOptions)}
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-primary transition-colors"
                >
                  <Sparkles size={16} />
                  {showDemoOptions ? 'Hide Demo Options' : 'Try Demo Accounts'}
                </button>
                
                {showDemoOptions && (
                  <div className="mt-4 space-y-3">
                    <p className="text-xs text-gray-400 text-center mb-3">
                      Quick access for demonstrations
                    </p>
                    {demoAccounts.map((account) => {
                      const Icon = account.icon;
                      return (
                        <button
                          key={account.email}
                          type="button"
                          onClick={() => handleDemoLogin(account)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-gray-100 hover:border-primary/30 hover:bg-primary/5 transition-all group"
                        >
                          <div className={`p-2 rounded-lg bg-gradient-to-br ${account.color}`}>
                            <Icon size={18} className="text-white" />
                          </div>
                          <div className="text-left flex-1">
                            <p className="font-semibold text-gray-800 group-hover:text-primary transition-colors">
                              {account.label}
                            </p>
                            <p className="text-xs text-gray-400">{account.description}</p>
                          </div>
                          <span className="text-xs text-gray-300 group-hover:text-primary">Click to fill →</span>
                        </button>
                      );
                    })}
                    <p className="text-xs text-gray-400 text-center mt-2">
                      Credentials will be auto-filled. Click &quot;Sign In&quot; to proceed.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;